/**
 * Strapi v5 Populate Query Builder
 * 
 * Centralizes population chains to avoid duplication and ensure consistency.
 * Converts nested objects to Strapi's bracket notation query string.
 * 
 * Usage:
 * ```
 * popAll() // "*"
 * populate({
 *   operations: {},
 *   address: {},
 *   placeTypes: {
 *     vehicleTypes: {},
 *     chargerType: {}
 *   }
 * })
 * ```
 */

interface PopulateConfig {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

/**
 * Builds nested populate query strings in Strapi v5 format
 * 
 * @param config - Nested object representing relations to populate
 * @param prefix - Internal parameter for recursion (default: 'populate')
 * @returns Query string like "populate[field1]=*&populate[field2][populate][subfield]=*"
 */
export function buildPopulate(config: PopulateConfig, prefix = 'populate'): string {
    const parts: string[] = [];

    Object.entries(config).forEach(([key, value]) => {
        if (Object.keys(value).length === 0) {
            // Leaf node - populate this field
            parts.push(`${prefix}[${key}]=*`);
        } else {
            // Branch node - recurse deeper
            const nestedPrefix = `${prefix}[${key}][populate]`;
            const nested = buildPopulate(value as PopulateConfig, nestedPrefix);
            parts.push(nested);
        }
    });

    return parts.join('&');
}

/**
 * Populates all relations (shorthand for common use case)
 */
export function popAll(): string {
    return 'populate=*';
}

/**
 * Preset: Parking list with all nested relations
 */
export const parkingListPopulate = buildPopulate({
    operations: {},
    address: {},
    contact: {},
    commercialInfos: {},
    lounges: {},
    placeTypes: {
        vehicleTypes: {},
        availableLounges: {},
        chargerType: {},
    },
    statuses: {},
});

/**
 * Preset: Parking detail (same as list for now)
 */
export const parkingDetailPopulate = parkingListPopulate;

/**
 * Preset: Reservation class with billing relations
 */
export const resaClassPopulate = buildPopulate({
    billToClient: {
        flatFees: {},
        powerSupplyFees: {},
    },
    config: {},
});

/**
 * Preset: Reservation class detail with full pricing breakdown
 */
export const resaClassDetailPopulate = buildPopulate({
    billToClient: {
        flatFees: {},
        powerSupplyFees: {},
    },
    billToEndUser: {
        eChargingPrices: {
            pricing: {
                eChargingPrices: {},
            },
        },
        timeslotsPrices: {
            flatDays: {},
            hours: {},
        },
    },
    config: {},
});

/**
 * Preset: Group with members and configs
 */
export const groupDetailPopulate = buildPopulate({
});

/**
 * Preset: Group list basic
 */
export const groupListPopulate = buildPopulate({
    groupConfigs: {},
});

/**
 * Preset: Group config type filter
 */
export const groupConfigTypePopulate = buildPopulate({
    relatesTo: {},
    groupConfigs: {},
    members: {},
    guests: {},
    packet: {},
});

/**
 * Preset: Reservation class by client with full pricing
 */
export const resaClassByClientPopulate = buildPopulate({
    billToClient: {
        flatFees: {},
        powerSupplyFees: {},
    },
    billToEndUser: {
        eChargingPrices: {
            pricing: {
                eChargingPrices: {},
            },
        },
        timeslotsPrices: {
            flatDays: {},
            hours: {},
        },
    },
    config: {},
});
